import React from "react";
import { useJobSetupStore } from "@presentation/stores/JobSetupStore";
import { useFileExplorerStore } from "@presentation/stores/FileExplorerStore";
import { SvgIcon } from "@progress/kendo-react-common";
import * as svgIcons from "@progress/kendo-svg-icons";

import { requiredFieldBorder, useRequiredFieldState } from "../jobRequirements";
import { RequiredFieldNote } from "./RequiredFieldIndicators";

export const RawFileFormBlock: React.FC = () => {
  const rawFiles = useJobSetupStore((state) => state.CreateJobState.rawFiles) || [];
  const selectedState = useFileExplorerStore((state) => state.selectedState);
  const setRightPanelTab = useJobSetupStore((state) => state.setRightPanelTab);
  const hasFiles = rawFiles.length > 0;
  const previewLimit = 50;

  const requiredState = useRequiredFieldState("rawFiles");

  const totalSize = rawFiles.reduce((accumulator, file) => accumulator + (file.sizeInBytes || 0), 0) / (1024 * 1024 * 1024);
  const selectedFolders = Object.entries(selectedState)
    .filter(([, meta]) => meta.isFolder)
    .map(([path]) => path);
  // Only keep the outermost selected folders; nested ones are already implied by their parent.
  const topLevelFolders = selectedFolders.filter((folder) => selectedFolders.every((other) => other === folder || !folder.startsWith(`${other}/`)));
  const isInsideSelectedFolder = (filePath: string) => selectedFolders.some((folder) => filePath.startsWith(`${folder}/`));
  // Files picked individually, i.e. not covered by any selected folder.
  const standaloneFiles = rawFiles.filter((file) => !isInsideSelectedFolder(file.path));
  const hasSelectedFolders = topLevelFolders.length > 0;
  const folderFileCount = rawFiles.length - standaloneFiles.length;

  const summaryParts: string[] = [];
  if (hasSelectedFolders) {
    summaryParts.push(`${topLevelFolders.length} ${topLevelFolders.length === 1 ? "folder" : "folders"} (${folderFileCount} ${folderFileCount === 1 ? "file" : "files"})`);
  }
  if (standaloneFiles.length > 0) {
    summaryParts.push(`${standaloneFiles.length} ${standaloneFiles.length === 1 ? "file" : "files"}`);
  }
  const summaryText = `${summaryParts.join(" + ")} selected (${rawFiles.length} ${rawFiles.length === 1 ? "file" : "files"} total, ${totalSize.toFixed(2)} GiB)`;

  const allEntries = [
    ...topLevelFolders.map((path) => ({ key: `folder:${path}`, label: path, isFolder: true })),
    ...standaloneFiles.map((file) => ({ key: `file:${file.path}`, label: file.path.split("/").pop() ?? file.path, isFolder: false })),
  ];
  const previewEntries = allEntries.slice(0, previewLimit);
  const hiddenEntriesCount = Math.max(0, allEntries.length - previewEntries.length);

  return (
    <fieldset className="k-form-fieldset">
      <legend className="k-form-legend">
        <strong>Raw Files</strong> <SvgIcon style={{ paddingBottom: "5px" }} icon={svgIcons.fileDataIcon} />
      </legend>

      <div style={{ marginBottom: "10px" }}>
        <div
          style={{
            border: requiredFieldBorder(requiredState),
            borderRadius: "3px",
            padding: "8px",
            minHeight: "32px",
            backgroundColor: "white",
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "4px",
            cursor: hasFiles ? "default" : "pointer",
          }}
          onClick={hasFiles ? undefined : () => setRightPanelTab(0)}
          title={hasFiles ? undefined : "Click to switch to the Raw Files tab"}
        >
          {hasFiles ? (
            <>
              <div style={{ width: "100%", fontWeight: "bold", marginBottom: "4px", fontSize: "12px", color: "#555" }}>
                {hasSelectedFolders ? summaryText : `${rawFiles.length} files selected (${totalSize.toFixed(2)} GiB)`}
              </div>
              <div style={{ maxHeight: "120px", overflowY: "auto", width: "100%" }}>
                {previewEntries.map((entry) => (
                  <span
                    key={entry.key}
                    style={{
                      display: "inline-block",
                      backgroundColor: "#e3f2fd",
                      border: "1px solid #2196f3",
                      borderRadius: "3px",
                      padding: "2px 8px",
                      fontSize: "12px",
                      color: "#1976d2",
                      margin: "2px",
                    }}
                    title={entry.label}
                  >
                    {entry.label}
                  </span>
                ))}
                {hiddenEntriesCount > 0 && (
                  <span
                    style={{
                      display: "inline-block",
                      backgroundColor: "#f3f4f6",
                      border: "1px solid #d1d5db",
                      borderRadius: "3px",
                      padding: "2px 8px",
                      fontSize: "12px",
                      color: "#4b5563",
                      margin: "2px",
                    }}
                  >
                    +{hiddenEntriesCount} more
                  </span>
                )}
              </div>
            </>
          ) : (
            <span style={{ fontStyle: "italic", fontSize: "13px" }}>
              Select <strong>Raw Files</strong> in the tab on the right.
            </span>
          )}
        </div>
        <RequiredFieldNote state={requiredState}>Select at least one raw file to continue.</RequiredFieldNote>
      </div>
    </fieldset>
  );
};
